import { useState, useEffect } from 'react';
import { SOURCE_MAP } from './Guess';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/material';

const isMobileOrTablet = () => {
  let check = false;
  //eslint-disable-next-line
  (function(a){if(/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i.test(a)||/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(a.substr(0,4))) check = true;})(navigator.userAgent||navigator.vendor);
  return check;
};

const DialogClose = (props) => {
	const { children, onClose, ...other } = props;

	return (
		<DialogContent sx={{ m: 0, p: 2 }} {...other}>
			{onClose ? (
				<IconButton
					aria-label="close"
					onClick={onClose}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500]
					}}
				>
					<CloseIcon />
				</IconButton>
			) : null}
		</DialogContent>
	);
};

const Stat = (props) => {
	return (
		<Box textAlign="center">
			<Typography variant="h5" textAlign="center">
				{props.stat}
			</Typography>
			<Typography variant="caption" textAlign="center">
				{props.label}
			</Typography>
		</Box>
	);
};

const Results = (props) => {
  const [ buttonText, setButtonText ] = useState("Share");

  const isLoading = props.isGameToday && !props.answer;

	const gamesPlayed = props.stats ? props.stats.gamesPlayed : 0;
	const gamesWonPercentage = props.stats ? props.stats.gamesWonPercentage : null;
	const streakCurrent = props.stats ? props.stats.streakCurrent : 0;
	const streakMax = props.stats ? props.stats.streakMax : 0;
	const nextGameTs = props.nextGameTs;

  let evaluations = [];
  for (const guess of props.guesses) {
    evaluations.push(guess.evaluation);
  }

	const [ secondsToNextGame, setSecondsToNextGame ] = useState(() => nextGameTs - +new Date() / 1000);

	useEffect(
		() => {
			setSecondsToNextGame(nextGameTs - +new Date() / 1000);
			let timer = setInterval(() => {
				setSecondsToNextGame((prevState) => prevState - 1);
			}, 1000);
			return () => clearInterval(timer);
		},
		[ nextGameTs ]
	);

	let hours = parseInt(secondsToNextGame / 60 / 60);
	let minutes = parseInt((secondsToNextGame - hours * 60 * 60) / 60);
	let seconds = parseInt((secondsToNextGame - hours * 60 * 60) % 60);
	hours = hours < 10 ? '0' + hours : hours;
	minutes = minutes < 10 ? '0' + minutes : minutes;
	seconds = seconds < 10 ? '0' + seconds : seconds;

	const countDownText = hours + ':' + minutes + ':' + seconds;

	const shareHandler = () => {
		if (navigator.share) {
      let text =
        `www.guessaulenko.com #${props.game_id} ${props.guesses.length}/${props.maxGuesses}`
        + '\n';

      // ⬛🟨🟩
      for (const evaluation of evaluations) {
        for (const col of props.columnConfig.columns) {
          const evaluationKey = SOURCE_MAP[col.source]?.evaluationKey ?? col.source;
          const result = evaluation[evaluationKey]?.result ?? 0;
          text += result === 2 ? '🟩' : result === 1 ? '🟨' : '⬛';
        }
        text += '\n';
      }
      text = text.trimEnd();

      if (!isMobileOrTablet()) {
        navigator.clipboard.writeText(text);
        setButtonText("Copied!");
      } else {
        navigator
          .share({ text: text })
          .then(() => setButtonText("Shared!"))
          .catch(() => setButtonText("Error!"));
      }
		} else {
      setButtonText("Error!");
		}
	};

  const closeHandler = () => {
    setButtonText("Share");
    props.onClose();
  };

	return (
		<Dialog open={props.open} onClose={closeHandler}>
			<DialogClose id="dial-title-results" onClose={closeHandler} />
			<DialogContent>
				{isLoading
          ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          : <Stack direction="column" spacing={2}>
					<Stack direction="column" justifyContent="center">
						{props.gameTypeName && (
							<Typography variant="subtitle2" textAlign="center" color="text.secondary">
								{props.gameTypeName}
							</Typography>
						)}
						<Typography variant="subtitle1" sx={{ textAlign: 'center' }}>
							{!props.isGameToday ? (
								'No Game Today'
							) : !props.completed ? (
								"Today's Game In Progress"
							) : props.correct ? (
								'Guessaulenko, you beauty!'
							) : (
								'Better luck next time!'
							)}
						</Typography>
						{(props.isGameToday && props.completed) && (
							<Typography variant="h5" sx={{ textAlign: 'center' }}>
								{props.answer && (props.answer.name + ' (' + props.answer.teams.split(";")[0] + ' #' + props.answer.numbers.split(";")[0] + ')')}
							</Typography>
						)}
					</Stack>
					<Stack direction="row" spacing={2} justifyContent="center">
						<Stat stat={gamesPlayed} label="Played" />
						<Stat stat={gamesWonPercentage ? Math.round(gamesWonPercentage * 100) + '%' : '-'} label="Win %" />
						<Stat stat={streakCurrent} label="Streak" />
						<Stat stat={streakMax} label="Max Streak" />
					</Stack>
					<Stack direction="row" spacing={3} width="100%" justifyContent="center">
						{(props.isGameToday && nextGameTs > 1) && (
							<Box textAlign="center">
								<Typography variant="caption" textAlign="center">
									Next Game
								</Typography>
								<Typography variant="h5" textAlign="center">
									{countDownText}
								</Typography>
							</Box>
						)}
						{(props.isGameToday && props.completed) && <Button variant="contained" color="success" fullWidth onClick={shareHandler}>
							{buttonText}
						</Button>}
					</Stack>
				</Stack>
        }
			</DialogContent>
		</Dialog>
	);
};

export default Results;
